import { useCallback, useRef } from 'react';

import type { UseResizeObserverProps } from './types';

export type { UseResizeObserverProps } from './types';

export const useResizeObserver = ({
  onResize,
  threshold = 0,
}: UseResizeObserverProps) => {
  const roRef = useRef<ResizeObserver | null>(null);
  const lastSizeRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const detachResizeObserver = useCallback(() => {
    roRef.current?.disconnect();
  }, []);

  const attachResizeObserver = useCallback(
    (element: HTMLElement) => {
      detachResizeObserver();

      const wrappedCallback: ResizeObserverCallback = (entries, observer) => {
        if (threshold <= 0) {
          // No threshold, call onResize immediately
          onResize(entries, observer);
          return;
        }

        // Check if size change exceeds threshold
        const entry = entries[0];
        if (!entry) {
          return;
        }

        const newWidth = Math.round(entry.contentRect.width);
        const newHeight = Math.round(entry.contentRect.height);
        const lastSize = lastSizeRef.current;

        if (
          Math.abs(newWidth - lastSize.width) >= threshold ||
          Math.abs(newHeight - lastSize.height) >= threshold
        ) {
          lastSizeRef.current = { width: newWidth, height: newHeight };
          onResize(entries, observer);
        }
      };

      const resizeObserver = new ResizeObserver(wrappedCallback);
      resizeObserver.observe(element);
      roRef.current = resizeObserver;
    },
    [onResize, detachResizeObserver, threshold],
  );

  const refCb = useCallback(
    (element: HTMLElement | null) => {
      if (element) {
        attachResizeObserver(element);
      } else {
        detachResizeObserver();
      }
    },
    [attachResizeObserver, detachResizeObserver],
  );

  return refCb;
};
